/**
 * Canonical 3-letter day token. Normalizes full names ("Monday") and
 * abbreviations ("Mon") — in any case — to the same value ("mon"). Used to
 * compare day-of-week values that may be stored in either form.
 */
export function dayToken(s: string): string {
  return s.trim().toLowerCase().slice(0, 3);
}

/**
 * Convert a local datetime to UTC.
 *
 * @param localDatetime - Naive local datetime string, e.g. "2026-02-12T08:15:00"
 * @param timezone - IANA timezone, e.g. "America/Los_Angeles"
 * @returns Date object in UTC
 *
 * @example
 * localToUtc('2026-02-12T08:15:00', 'America/Los_Angeles')
 * // → Date representing 2026-02-12T16:15:00Z (PST is UTC-8)
 */
export function localToUtc(localDatetime: string, timezone: string): Date {
  // Treat the input as if it were UTC so we can work with it numerically
  const asUtc = new Date(localDatetime + (localDatetime.endsWith('Z') ? '' : 'Z'));

  // An offset-bearing string ("…T09:00:00-04:00") becomes "…-04:00Z" above and
  // parses to Invalid Date, which Intl then rejects with a message that names
  // neither the value nor the caller. Say what was wrong with WHICH input —
  // callers with an absolute instant want hasZoneDesignator(), not this.
  if (Number.isNaN(asUtc.getTime())) {
    throw new Error(
      `localToUtc expects a naive local datetime, got ${JSON.stringify(localDatetime)}`
    );
  }

  // Ask Intl what the local time would be at this UTC instant
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = fmt.formatToParts(asUtc);
  const get = (type: string): number => parseInt(parts.find((p) => p.type === type)?.value ?? '0');

  const localAsUtcMs = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second')
  );

  // offset = (what Intl says local is) - (actual UTC) → positive for east of UTC
  const offsetMs = localAsUtcMs - asUtc.getTime();

  // The real UTC = naiveUtc - offset
  return new Date(asUtc.getTime() - offsetMs);
}

/**
 * The local calendar date (YYYY-MM-DD) of a UTC instant, in a timezone.
 *
 * The inverse companion to {@link localToUtc}: together they let a caller go
 * instant → local date → naive local datetime → instant without ever doing
 * offset arithmetic by hand.
 *
 * @example
 * localDateInTimezone(new Date('2026-08-04T02:00:00Z'), 'America/Los_Angeles')
 * // → '2026-08-03' (still the 3rd locally, 19:00 PDT)
 */
export function localDateInTimezone(date: Date, timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: timezone,
  }).formatToParts(date);
  const get = (type: string): string => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/**
 * Check whether a datetime string is already in UTC (ends with Z or +00:00).
 */
export function isUtc(datetime: string): boolean {
  return datetime.endsWith('Z') || datetime.endsWith('+00:00') || datetime.endsWith('+0000');
}

/**
 * True when an ISO 8601 datetime carries an explicit zone designator — `Z` or a
 * numeric `±HH:MM` / `±HHMM` offset — and therefore denotes an absolute instant
 * that `new Date()` parses correctly on its own.
 *
 * This, not {@link isUtc}, is the right question for "parse directly, or
 * interpret in a timezone?". `isUtc` asks the narrower "is this UTC?", and
 * answers false for a DST-correct local offset like `2026-09-24T09:00:00-04:00`
 * — an absolute instant that is not UTC. Routing such a string to
 * {@link localToUtc} yields an Invalid Date, which is how every athenaOne
 * appointment failed to sync (the connector stamps departmental offsets).
 */
export function hasZoneDesignator(datetime: string): boolean {
  return /[zZ]$/.test(datetime) || /[+-]\d{2}:?\d{2}$/.test(datetime);
}
